import Cookies from 'js-cookie';

import type { AwsWafConfig, AwsWafRemoteConfig } from '../types';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { addBreadcrumb } from '../utilities/analytics/error-reporting';
import { MAX_AWS_CAPTCHA_FAILURES } from './aws-captcha.constants';

// The AWS WAF SDK sets the token as a cookie named 'aws-waf-token'.
// See: https://docs.aws.amazon.com/waf/latest/developerguide/waf-tokens-details.html
const AWS_CAPTCHA_TOKEN_COOKIE_NAME = 'aws-waf-token';

/**
 * Whether `hostname` is covered by `tokenDomain` — either an exact match or a subdomain of it.
 * The suffix match handles e.g. `id.<label>.atlassian-isolated.net` being covered by a token
 * domain of `<label>.atlassian-isolated.net`.
 */
const covers = (tokenDomain: string, hostname: string): boolean =>
  hostname === tokenDomain || hostname.endsWith(`.${tokenDomain}`);

/**
 * Selects the API key whose token domains cover `hostname`, or `null` when none match.
 *
 * We deliberately do NOT fall back to the first key: AWS mints the WAF token against the key's
 * domain and the WebACL only honours tokens whose domain is in its TokenDomains. A non-matching
 * key would produce a token the WAF rejects, challenging the user forever. Returning `null`
 * (no CAPTCHA rendered) is strictly better than the wrong key.
 *
 * Called once at the fetch boundary (see `fetchAwsWafConfig`) — the resolved key is then cached
 * in the store, so consumers read a flat `apiKey` rather than re-selecting on every use.
 */
export function selectAwsWafApiKey(
  config: AwsWafRemoteConfig | null | undefined,
  hostname: string = window.location.hostname
): string | null {
  return (
    config?.apiKeys?.find(key => key.tokenDomains.some(domain => covers(domain, hostname)))
      ?.apiKey ?? null
  );
}

/**
 * Returns `true` (and narrows the type) when both required fields of the resolved config are
 * present and non-empty. FedRAMP Moderate exclusion and hostname key selection are handled once
 * at the fetch boundary (see `fetchAwsWafConfig`), so an invalid/unavailable config is already
 * `undefined` by the time it reaches here.
 */
export function isValidAwsWafConfig(
  awsWafConfig: AwsWafConfig | null | undefined
): awsWafConfig is AwsWafConfig {
  return !!(awsWafConfig?.integrationUrl && awsWafConfig?.apiKey);
}

/**
 * Optional WAF headers for login requests.
 * `test-captcha` forces a 405 in E2E when the host-headers gate is on.
 * `atl-edge-auth-challenge-rollout` opts the request into the initial AWS Captcha ruleset.
 */
export function getAwsCaptchaWafHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  if (checkFeatureGate(AidFrontendFeatureGate.ENABLE_HOST_HEADERS_FOR_AWS_CAPTCHA)) {
    headers['test-captcha'] = 'true';
  }

  if (checkFeatureGate(AidFrontendFeatureGate.AWS_CAPTCHA_INITIAL_RULESET)) {
    headers['atl-edge-auth-challenge-rollout'] = '1';
  }

  return headers;
}

/**
 * Function to get the value from the token cookie, ensuring safety in case the function throws an error.
 *
 * @returns String
 */
export function getAwsCaptchaTokenValue() {
  try {
    const value = Cookies.get(AWS_CAPTCHA_TOKEN_COOKIE_NAME) || '';

    if (!value || typeof value !== 'string') {
      return '';
    }

    return value;
  } catch (e) {
    // Make sure we return a value even if there was an error
    return '';
  }
}

export type OnShowAwsCaptchaModalCallbackProps = {
  /** Function called when the puzzle is successfully completed. */
  onSuccess: () => void;
};

type Opts = {
  awsWafConfig: AwsWafConfig | undefined;
  /**
   * A callback that renders the AWS Captcha puzzle. Used when a 405 is returned from the WAF
   * which indicates the user must complete a captcha puzzle before
   * the request can be retried.
   */
  onShowAwsCaptchaModal: ({ onSuccess }: OnShowAwsCaptchaModalCallbackProps) => void;
  /**
   * What to do when user successfully completes the puzzle.
   * Receives the updated consecutive-405 failure count to thread into the retried request.
   */
  retry: (nextAwsCaptchaFailureCount: number) => void;
  /**
   * Adds context to breadcrumb logs
   */
  breadcrumbTag: string;
  /**
   * Anything additional to do when config is not valid
   */
  onInvalidConfig?: () => void;
  /**
   * Prior consecutive WAF 405 / CAPTCHA failures for this request flow.
   * Omitted on the initial attempt; set only after a solved CAPTCHA is retried.
   */
  awsCaptchaFailureCount?: number;
  /**
   * Called when another CAPTCHA would exceed {@link MAX_AWS_CAPTCHA_FAILURES}.
   * The initial 405 counts as the first failure, so MAX consecutive 405s abort the flow.
   */
  onFailureLimitReached: () => void;
};

type CaptchaHandling =
  | {
      shouldHandle: true;
      handler: () => void;
    }
  | {
      shouldHandle: false;
    };

export const buildAwsCaptchaHandling = (
  responseStatusCode: number,
  opts: Opts
): CaptchaHandling => {
  const {
    awsWafConfig,
    onShowAwsCaptchaModal,
    retry,
    onInvalidConfig,
    breadcrumbTag,
    awsCaptchaFailureCount,
    onFailureLimitReached,
  } = opts;

  // 405 is returned only by AWS Captcha in the situation where the WAF is asking the user to complete a captcha. We also require config to be present — if the gate is on but config hasn't arrived from the backend yet,
  // we can't render the puzzle, so we fall through to normal error handling rather than showing an empty modal.
  const isAwsCaptchaConfigured = isValidAwsWafConfig(awsWafConfig);

  // Whether the AWS Captcha feature is enabled
  const isAwsCaptchaEnabled = checkFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);

  if (isAwsCaptchaEnabled && !isAwsCaptchaConfigured) {
    addBreadcrumb({
      category: 'login.show_captcha',
      message: `Captcha required for ${breadcrumbTag} but awsWafConfig is absent`,
      level: 'warning',
    });
    onInvalidConfig?.();
    return { shouldHandle: false };
  }

  const shouldHandle = isAwsCaptchaEnabled && isAwsCaptchaConfigured && responseStatusCode === 405;

  if (!shouldHandle) {
    return { shouldHandle: false };
  }

  // The initial 405 counts as the first failed attempt, so MAX_AWS_CAPTCHA_FAILURES
  // consecutive 405s to this endpoint aborts the flow (rather than MAX + 1).
  const nextAwsCaptchaFailureCount =
    awsCaptchaFailureCount === undefined ? 1 : awsCaptchaFailureCount + 1;

  if (nextAwsCaptchaFailureCount >= MAX_AWS_CAPTCHA_FAILURES) {
    return {
      shouldHandle: true,
      handler: () => {
        addBreadcrumb({
          category: 'login.show_captcha.failure_limit_reached',
          message: `AWS CAPTCHA failure limit reached (${MAX_AWS_CAPTCHA_FAILURES}) — aborting`,
          level: 'error',
          data: { awsCaptchaFailureCount: nextAwsCaptchaFailureCount },
        });
        onFailureLimitReached();
      },
    };
  }

  const handler = () => {
    addBreadcrumb({
      category: 'login.show_captcha',
      message: `Captcha shown for ${breadcrumbTag}`,
      level: 'info',
      data: { awsCaptchaFailureCount: nextAwsCaptchaFailureCount },
    });
    onShowAwsCaptchaModal({
      onSuccess: () => {
        retry(nextAwsCaptchaFailureCount);

        addBreadcrumb({
          category: 'login.show_captcha.puzzle_completed',
          message: `Captcha completed for ${breadcrumbTag}`,
          level: 'info',
        });
      },
    });
    return;
  };

  return { shouldHandle, handler };
};
